import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import Chat, { Bubble, useMessages } from "@chatui/core";
import "@chatui/core/dist/index.css";
import "./styles/theme.css";
import BubbleButton from "./components/BubbleButton";
import ChatFlowEngine from "./utils/chatFlowEngine";
import type { ChatFlow } from "./types/flow";
import type { Locale } from "./types/locale";
import { UI_COPY, type UICopy } from "./content/i18n";
import { getInitialLocale, persistLocale } from "./utils/locale";
import {
  getPixelIdsFromQuery,
  getCtaUrlFromQuery,
  getEffectiveCtaUrl,
  initFacebookPixels,
  initGTM,
  trackCtaClick,
} from "./utils/marketing";
import jobHiringFlowEn from "./flows/job-hiring-en-ph.json";
import jobHiringFlowFil from "./flows/job-hiring-fil-ph.json";

const FLOW_BY_LOCALE: Record<Locale, ChatFlow> = {
  en: jobHiringFlowEn as ChatFlow,
  fil: jobHiringFlowFil as ChatFlow,
};

interface LocalizedChatSessionProps {
  locale: Locale;
  uiCopy: UICopy;
}

function LocalizedChatSession({ locale, uiCopy }: LocalizedChatSessionProps) {
  const { messages, appendMsg, setTyping, deleteMsg } = useMessages([]);
  const [flowEngine, setFlowEngine] = useState<ChatFlowEngine | null>(null);
  const [streamingText, setStreamingText] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [dynamicCtaUrl, setDynamicCtaUrl] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const activeTimersRef = useRef<number[]>([]);
  const flow = FLOW_BY_LOCALE[locale];

  const schedule = useCallback((callback: () => void, delay: number) => {
    const timerId = window.setTimeout(() => {
      activeTimersRef.current = activeTimersRef.current.filter(
        (activeTimerId) => activeTimerId !== timerId
      );

      if (isMountedRef.current) {
        callback();
      }
    }, delay);

    activeTimersRef.current.push(timerId);
    return timerId;
  }, []);

  const wait = useCallback(
    (delay: number) =>
      new Promise<void>((resolve) => {
        schedule(resolve, delay);
      }),
    [schedule]
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      activeTimersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      activeTimersRef.current = [];
    };
  }, []);

  const streamText = useCallback(
    async (text: string) => {
      const words = text.split(" ");
      let currentText = "";

      if (!isMountedRef.current) {
        return;
      }

      setIsStreaming(true);
      setStreamingText("");

      for (let index = 0; index < words.length; index += 1) {
        if (!isMountedRef.current) {
          return;
        }

        currentText += `${index > 0 ? " " : ""}${words[index]}`;
        setStreamingText(currentText);
        await wait(50);
      }

      if (!isMountedRef.current) {
        return;
      }

      setIsStreaming(false);
      appendMsg({
        type: "text",
        content: { text },
        position: "left",
      });
      setStreamingText("");
    },
    [appendMsg, wait]
  );

  useEffect(() => {
    let cancelled = false;

    const initializeFlow = async () => {
      try {
        initGTM();

        const pixelIds = getPixelIdsFromQuery();
        if (pixelIds.length > 0) {
          initFacebookPixels(pixelIds);
        }

        setDynamicCtaUrl(getCtaUrlFromQuery());

        const validation = ChatFlowEngine.validateFlow(flow);
        if (!validation.valid) {
          console.error("Invalid flow:", validation.errors);
          return;
        }

        const engine = new ChatFlowEngine(flow);
        setFlowEngine(engine);

        const initialMessage = engine.getInitialMessage();

        schedule(async () => {
          if (cancelled || !isMountedRef.current) {
            return;
          }

          await streamText(initialMessage.content.text);

          if (
            !cancelled &&
            isMountedRef.current &&
            initialMessage.content.options
          ) {
            appendMsg({
              type: "quick-replies",
              content: { options: initialMessage.content.options },
              position: "left",
            });
          }
        }, 500);
      } catch (error) {
        console.error("Failed to initialize flow:", error);
      }
    };

    initializeFlow();

    return () => {
      cancelled = true;
    };
  }, [appendMsg, flow, schedule, streamText]);

  const showRestartOption = useCallback(() => {
    schedule(() => {
      if (!isMountedRef.current) {
        return;
      }

      appendMsg({
        type: "quick-replies",
        content: {
          options: [
            {
              label: `🔄 ${uiCopy.restartLabel}`,
              value: "restart",
              nextNode: "welcomeMessage",
            },
          ],
        },
        position: "left",
      });
    }, 2000);
  }, [appendMsg, schedule, uiCopy.restartLabel]);

  const handleQuickReplyClick = useCallback(
    (optionValue: string, optionLabel: string) => {
      if (!flowEngine) {
        return;
      }

      const quickReplyMsgIndex = messages.findIndex(
        (msg) => msg.type === "quick-replies"
      );

      if (quickReplyMsgIndex !== -1) {
        deleteMsg(messages[quickReplyMsgIndex]._id);
      }

      appendMsg({
        type: "text",
        content: { text: optionLabel },
        position: "right",
      });

      setTyping(true);

      schedule(async () => {
        try {
          const nextMessages = flowEngine.processUserSelection(optionValue);
          setTyping(false);

          if (nextMessages.length === 1) {
            return;
          }

          const [, botMessage] = nextMessages;
          await streamText(botMessage.content.text);

          if (botMessage.content.options) {
            appendMsg({
              type: "quick-replies",
              content: { options: botMessage.content.options },
              position: "left",
            });
            return;
          }

          if (flowEngine.isCompleted()) {
            showRestartOption();
          }
        } catch (error) {
          console.error("Error processing selection:", error);
          setTyping(false);
        }
      }, 800);
    },
    [
      appendMsg,
      deleteMsg,
      flowEngine,
      messages,
      schedule,
      setTyping,
      showRestartOption,
      streamText,
    ]
  );

  const handleRestart = useCallback(() => {
    if (!flowEngine) {
      return;
    }

    const quickReplyMsgIndex = messages.findIndex(
      (msg) => msg.type === "quick-replies"
    );

    if (quickReplyMsgIndex !== -1) {
      deleteMsg(messages[quickReplyMsgIndex]._id);
    }

    flowEngine.reset();
    const initialMessage = flowEngine.getInitialMessage();

    schedule(async () => {
      await streamText(initialMessage.content.text);

      if (initialMessage.content.options) {
        appendMsg({
          type: "quick-replies",
          content: { options: initialMessage.content.options },
          position: "left",
        });
      }
    }, 500);
  }, [appendMsg, deleteMsg, flowEngine, messages, schedule, streamText]);

  const renderMessageContent = (msg: any) => {
    if (msg.type === "quick-replies") {
      return (
        <div className="quick-replies-inline">
          {msg.content.options.map((option: any, index: number) => {
            const isRestart = option.value === "restart";
            const effectiveUrl = option.isCTA
              ? getEffectiveCtaUrl(dynamicCtaUrl, option.url)
              : undefined;

            return (
              <BubbleButton
                key={`${option.value}-${index}`}
                label={option.label}
                icon={option.icon}
                url={effectiveUrl || undefined}
                isCTA={option.isCTA}
                originalUrl={option.url}
                onClick={() => {
                  if (isRestart) {
                    handleRestart();
                  } else {
                    handleQuickReplyClick(option.value, option.label);
                  }
                }}
              />
            );
          })}
        </div>
      );
    }

    return <Bubble content={msg.content.text} />;
  };

  const displayMessages = isStreaming
    ? [
        ...messages,
        {
          _id: "streaming",
          type: "text",
          content: { text: streamingText },
          position: "left" as const,
        },
      ]
    : messages;

  return (
    <>
      <Chat
        navbar={{ title: "" }}
        messages={displayMessages}
        renderMessageContent={renderMessageContent}
        placeholder={uiCopy.placeholder}
        onSend={() => {}}
      />
      <div className="cta-container">
        <button
          className="cta-button cta_click"
          onClick={() => {
            try {
              window.sendLpEvent?.("click");
            } catch (error) {
              void error;
            }

            trackCtaClick();
            const effectiveUrl = getEffectiveCtaUrl(dynamicCtaUrl, undefined);

            if (!effectiveUrl) {
              return;
            }

            schedule(() => {
              window.open(effectiveUrl, "_blank", "noopener,noreferrer");
            }, 100);
          }}
        >
          {uiCopy.bottomCtaLabel}
        </button>
      </div>
    </>
  );
}

function App() {
  const [locale, setLocale] = useState<Locale>(() => getInitialLocale());
  const uiCopy = UI_COPY[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const handleLocaleChange = useCallback((nextLocale: Locale) => {
    if (nextLocale === locale) {
      return;
    }

    persistLocale(nextLocale);
    startTransition(() => {
      setLocale(nextLocale);
    });
  }, [locale]);

  return (
    <div className="app-container" lang={locale}>
      <div className="custom-navbar">
        <div className="navbar-brand">
          <img src="/ek-logo.png" alt="Ekerja logo" className="navbar-logo" />
          <span className="navbar-title">Ekerja</span>
        </div>
        <div
          className="language-switcher"
          role="group"
          aria-label={uiCopy.languageSwitcherLabel}
        >
          <button
            type="button"
            className={`language-switcher__button ${
              locale === "en" ? "language-switcher__button--active" : ""
            }`}
            onClick={() => handleLocaleChange("en")}
            aria-pressed={locale === "en"}
            aria-label={uiCopy.switchToEnglishLabel}
          >
            EN
          </button>
          <button
            type="button"
            className={`language-switcher__button ${
              locale === "fil" ? "language-switcher__button--active" : ""
            }`}
            onClick={() => handleLocaleChange("fil")}
            aria-pressed={locale === "fil"}
            aria-label={uiCopy.switchToFilipinoLabel}
          >
            FIL
          </button>
        </div>
      </div>
      <LocalizedChatSession key={locale} locale={locale} uiCopy={uiCopy} />
    </div>
  );
}

export default App;
